import { useEffect, useMemo, useRef, useState } from 'react';
import {
    ILeoChatFile,
    ILeoChatOption,
    ILeoChatResponse,
    ILeoChatSelectedClient,
    useLazyGetAllUserConfigsQuery,
    useSendLeoChatMessageMutation,
    useUploadLeoChatFilesMutation,
} from '../../../store/chatbox/chatboxApi';
import {
    IComparisonBoardClientOption,
    useGetComparisonBoardClientsQuery,
} from '../../../store/users/usersApi';
import Toast from '../../../shared/toast';
import LeoChatInput from './components/LeoChatInput';
import LeoChatMessages, {
    LeoChatAgentMessage,
    LeoChatMessage,
    LeoChatPropertyOption,
    LeoChatPropertySaveSelection,
    LeoChatUserMessage,
} from './components/LeoChatMessages';

const normalizeResponsePayload = (response: ILeoChatResponse | any): ILeoChatResponse => {
    return response?.data?.data || response?.data || response || {};
};

const toAgentMessage = (response: ILeoChatResponse | any): LeoChatAgentMessage => {
    const payload = normalizeResponsePayload(response);
    const agent: any = payload?.agent || {};

    return {
        ...payload,
        ...agent,
        _id: payload?._id || payload?.agentMessageId || payload?.metadata?.messageId || agent?._id,
        topicId: payload?.topicId || payload?.chatId || payload?.topic || agent?.topicId || agent?.chatId || agent?.topic,
        message: payload?.message || agent?.message || payload?.summary || (typeof payload?.text === 'string' ? payload.text : ''),
        files: payload?.files || agent?.files || [],
        options: payload?.options || agent?.options || [],
        stage: payload?.stage || agent?.stage,
        responseType: payload?.responseType || agent?.responseType,
        summary: payload?.summary || agent?.summary,
        text: payload?.text || agent?.text,
        rows: payload?.rows || agent?.rows,
        columns: payload?.columns || agent?.columns,
        meta: payload?.meta || agent?.meta,
        createdAt: payload?.createdAt || agent?.createdAt || new Date(),
    };
};

const toUploadedFile = (file: ILeoChatFile): ILeoChatFile => ({
    ...file,
    url: file.url || file.link,
    link: file.link || file.url,
});

const toClientPayload = (client: IComparisonBoardClientOption): ILeoChatSelectedClient => ({
    id: client.id,
    label: client.label,
    username: client.username,
    companySub: client.companySub,
    companyName: client.companyName,
    name: client.name,
});

const getOptionQuestion = (option: ILeoChatOption) => {
    if (typeof option === 'string') return option.trim();

    return String(
        option?.value
        || option?.label
        || option?.text
        || option?.message
        || option?.title
        || option?.name
        || (typeof option?.key === 'string' ? option.key : ''),
    ).trim();
};

const getClientIdentifier = (client: ILeoChatSelectedClient) =>
    client.username || client.companySub || client.id || client.label;

const resolveClientParam = (clients: ILeoChatSelectedClient[]) =>
    clients.map(getClientIdentifier).filter(Boolean).join(',');

const isClientSelectStage = (stage?: string) =>
    stage === 'clients_select' || stage === 'awaiting_client';

const normalizeText = (value: unknown) => String(value || '').trim();

const getClientLabel = (client: ILeoChatSelectedClient) =>
    client.label || client.companyName || client.username || client.id;

const getConfigItemId = (item: any, fallback: string) =>
    normalizeText(item?._id || item?.id || item?.configId || fallback);

const getConfigPropertyName = (item: any) =>
    normalizeText(item?.propertyName || item?.companyName || item?.property || item?.name);

const mapConfigsToProperties = (
    configItems: any,
    client: ILeoChatSelectedClient,
): LeoChatPropertyOption[] => {
    const items = Array.isArray(configItems) ? configItems : [];
    const clientLabel = getClientLabel(client);
    const parentNamesById = new Map<string, string>();

    items.forEach((item: any, index: number) => {
        const parentId = normalizeText(item?.scenario?.parentId);
        if (parentId) return;

        const id = getConfigItemId(item, `${client.id}-${index}`);
        const name = getConfigPropertyName(item);
        if (id && name) parentNamesById.set(id, name);
    });

    return items
        .map((item: any, index: number): LeoChatPropertyOption | null => {
            const id = getConfigItemId(item, `${client.id}-${index}`);
            const parentPropertyId = normalizeText(item?.scenario?.parentId) || undefined;
            const scenarioName = normalizeText(item?.scenario?.name || item?.scenarioName) || undefined;
            const basePropertyName = getConfigPropertyName(item);
            const isScenario = Boolean(parentPropertyId);
            const parentPropertyName = parentPropertyId
                ? parentNamesById.get(parentPropertyId) || basePropertyName || undefined
                : undefined;
            const displayName = isScenario
                ? (scenarioName || basePropertyName || 'Scenario')
                : basePropertyName;

            if (!id || !displayName) return null;

            const label = isScenario
                ? `${parentPropertyName || basePropertyName || 'Property'} / ${displayName} - ${clientLabel}`
                : `${displayName} - ${clientLabel}`;

            return {
                value: `${client.id}::${id}`,
                label,
                propertyId: id,
                propertyName: basePropertyName || displayName,
                displayName,
                clientId: client.id,
                clientLabel,
                clientIdentifier: getClientIdentifier(client),
                type: isScenario ? 'scenario' : 'property',
                parentPropertyId,
                parentPropertyName,
                scenarioName,
            };
        })
        .filter(Boolean) as LeoChatPropertyOption[];
};

const buildPropertyQuestion = (selection: LeoChatPropertySaveSelection) => {
    if (selection.type === 'all') return 'all';

    const byClient = new Map<string, string[]>();
    selection.properties.forEach((property) => {
        const propertyLabel = property.type === 'scenario'
            ? `${property.parentPropertyName || property.propertyName} / ${property.scenarioName || property.displayName}`
            : property.displayName;
        const existing = byClient.get(property.clientLabel) || [];
        existing.push(propertyLabel);
        byClient.set(property.clientLabel, existing);
    });

    return Array.from(byClient.entries())
        .map(([clientLabel, propertyLabels]) => `${clientLabel}: ${propertyLabels.join(', ')}`)
        .join('; ');
};

const buildPropertyUserMessage = (selection: LeoChatPropertySaveSelection) => {
    if (selection.type === 'all') return 'Saved properties: All properties';
    return `Saved properties: ${buildPropertyQuestion(selection)}`;
};

const getResponseTopicId = (response: ILeoChatResponse | any) => {
    const payload = normalizeResponsePayload(response);
    const agent: any = payload?.agent || {};

    return payload?.topicId
        || payload?.chatId
        || payload?.topic
        || agent?.topicId
        || agent?.chatId
        || agent?.topic;
};

const LeoAvatar = ({ size = 'md' }: { size?: 'sm' | 'md' }) => (
    <div
        className={`flex shrink-0 items-center justify-center rounded-full bg-[#00330f] font-bold text-white ${
            size === 'sm' ? 'h-8 w-8 text-[11px]' : 'h-10 w-10 text-sm'
        }`}
        aria-hidden='true'
    >
        Leo
    </div>
);

const LeoChatPage = () => {
    const bottomRef = useRef<HTMLDivElement | null>(null);
    const [messages, setMessages] = useState<LeoChatMessage[]>([]);
    const [topicId, setTopicId] = useState<string>();
    const [selectedClients, setSelectedClients] = useState<ILeoChatSelectedClient[]>([]);
    const [properties, setProperties] = useState<LeoChatPropertyOption[]>([]);
    const [loadingProperties, setLoadingProperties] = useState(false);
    const [propertiesLoadError, setPropertiesLoadError] = useState<string>();
    const [isWaiting, setIsWaiting] = useState(false);
    const [savingClientSelect, setSavingClientSelect] = useState(false);
    const [savingPropertySelect, setSavingPropertySelect] = useState(false);
    const [toast, setToast] = useState<{ type: string; message: string } | null>(null);

    const [sendLeoChatMessage] = useSendLeoChatMessageMutation();
    const [uploadLeoChatFiles] = useUploadLeoChatFilesMutation();
    const [fetchClientConfigs] = useLazyGetAllUserConfigsQuery();
    const {
        data: clients = [],
        isFetching: loadingClients,
    } = useGetComparisonBoardClientsQuery();

    const lastAgentMessage = useMemo(() => {
        for (let index = messages.length - 1; index >= 0; index -= 1) {
            if (messages[index]?.agent) return messages[index].agent;
        }
        return undefined;
    }, [messages]);

    useEffect(() => {
        const timer = setTimeout(() => {
            bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 80);

        return () => clearTimeout(timer);
    }, [messages, isWaiting]);

    useEffect(() => {
        let isActive = true;

        if (!selectedClients.length) {
            setProperties([]);
            setPropertiesLoadError(undefined);
            setLoadingProperties(false);
            return () => {
                isActive = false;
            };
        }

        const loadProperties = async () => {
            setLoadingProperties(true);
            setPropertiesLoadError(undefined);

            try {
                const propertyGroups = await Promise.all(
                    selectedClients.map(async (client) => {
                        const response = await fetchClientConfigs(client.id).unwrap();
                        return mapConfigsToProperties(response?.data, client);
                    }),
                );

                if (!isActive) return;

                const nextProperties = propertyGroups
                    .flat()
                    .sort((a, b) =>
                        `${a.clientLabel} ${a.displayName}`.localeCompare(
                            `${b.clientLabel} ${b.displayName}`,
                            undefined,
                            { sensitivity: 'base' },
                        ));

                setProperties(nextProperties);
            } catch (error: any) {
                console.error(error);
                if (!isActive) return;
                setProperties([]);
                setPropertiesLoadError(
                    error?.data?.message || error?.error || 'Unable to load properties for selected clients.',
                );
            } finally {
                if (isActive) setLoadingProperties(false);
            }
        };

        loadProperties();

        return () => {
            isActive = false;
        };
    }, [fetchClientConfigs, selectedClients]);

    const appendAgentResponse = (response: ILeoChatResponse | any) => {
        const payload = normalizeResponsePayload(response);
        const agentMessage = toAgentMessage(payload);
        const nextTopicId = getResponseTopicId(payload);

        if (nextTopicId) {
            setTopicId(String(nextTopicId));
        }

        if (
            !agentMessage.message
            && !agentMessage.summary
            && !agentMessage.text
            && !agentMessage.rows?.length
            && !agentMessage.stage
            && !agentMessage.options?.length
        ) return;

        setMessages((prev) => [
            ...prev,
            {
                agent: agentMessage,
            },
        ]);
    };

    const sendMessage = async (
        message: string,
        files: ILeoChatFile[] = [],
        stage?: string,
    ) => {
        const question = message.trim();
        if (!question) {
            setToast({ type: 'error', message: 'Enter a question for Leo.' });
            return;
        }

        const client = resolveClientParam(selectedClients);
        const nextStage = stage || lastAgentMessage?.stage;

        const userMessage: LeoChatUserMessage = {
            message: question,
            files,
            stage,
            createdAt: new Date(),
        };

        setMessages((prev) => [...prev, { user: userMessage }]);
        setIsWaiting(true);

        try {
            const response = await sendLeoChatMessage({
                question,
                ...(topicId ? { topicId } : {}),
                ...(nextStage ? { stage: nextStage } : {}),
                ...(client ? { client } : {}),
            }).unwrap();

            appendAgentResponse(response);
        } catch (error: any) {
            console.error(error);
            setToast({
                type: 'error',
                message: error?.data?.message || error?.error || 'Unable to send message.',
            });
        } finally {
            setIsWaiting(false);
        }
    };

    const uploadFiles = async (files: File[], activeTopicId?: string) => {
        if (!files.length) return [];

        const formData = new FormData();
        files.forEach((file) => formData.append('files', file));
        if (activeTopicId) formData.append('topicId', activeTopicId);

        const uploaded = await uploadLeoChatFiles(formData).unwrap();
        return uploaded.map(toUploadedFile);
    };

    const onClickOption = async (option: ILeoChatOption) => {
        const optionMessage = getOptionQuestion(option);
        if (!optionMessage) return;

        await sendMessage(optionMessage, []);
    };

    const onSaveClients = async (selectedClients: IComparisonBoardClientOption[]) => {
        if (!selectedClients.length) {
            setToast({ type: 'error', message: 'Select at least one client.' });
            return;
        }

        const clientsPayload = selectedClients.map(toClientPayload);
        const clientNames = clientsPayload.map((client) => client.label).join(', ');
        const client = resolveClientParam(clientsPayload);
        const stage = lastAgentMessage?.stage || 'awaiting_client';
        const question = clientNames;

        setSavingClientSelect(true);
        try {
            setSelectedClients(clientsPayload);
            setMessages((prev) => [
                ...prev,
                {
                    user: {
                        message: `Saved clients: ${clientNames}`,
                        stage,
                        createdAt: new Date(),
                    },
                },
            ]);

            if (isClientSelectStage(stage)) {
                setIsWaiting(true);
                const response = await sendLeoChatMessage({
                    question,
                    ...(topicId ? { topicId } : {}),
                    stage,
                    ...(client ? { client } : {}),
                }).unwrap();

                appendAgentResponse(response);
            }

            setToast({ type: 'success', message: 'Clients saved.' });
        } catch (error: any) {
            console.error(error);
            setToast({
                type: 'error',
                message: error?.data?.message || error?.error || 'Unable to save clients.',
            });
        } finally {
            setSavingClientSelect(false);
            setIsWaiting(false);
        }
    };

    const onSaveProperties = async (selection: LeoChatPropertySaveSelection) => {
        if (!selectedClients.length) {
            setToast({ type: 'error', message: 'Select at least one client first.' });
            return;
        }

        if (selection.type === 'properties' && selection.properties.length === 0) {
            setToast({ type: 'error', message: 'Select at least one property.' });
            return;
        }

        const stage = lastAgentMessage?.stage || 'awaiting_property';
        const question = buildPropertyQuestion(selection);
        const client = resolveClientParam(selectedClients);

        setSavingPropertySelect(true);
        try {
            setMessages((prev) => [
                ...prev,
                {
                    user: {
                        message: buildPropertyUserMessage(selection),
                        stage,
                        createdAt: new Date(),
                    },
                },
            ]);

            setIsWaiting(true);
            const response = await sendLeoChatMessage({
                question,
                ...(topicId ? { topicId } : {}),
                stage,
                ...(client ? { client } : {}),
            }).unwrap();

            appendAgentResponse(response);
            setToast({ type: 'success', message: 'Properties saved.' });
        } catch (error: any) {
            console.error(error);
            setToast({
                type: 'error',
                message: error?.data?.message || error?.error || 'Unable to save properties.',
            });
        } finally {
            setSavingPropertySelect(false);
            setIsWaiting(false);
        }
    };

    return (
        <>
            <div className='flex h-[calc(100vh-150px)] min-h-[620px] w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-borderColor-darkMode dark:bg-bgColor-darkMode'>
                <div className='min-h-0 flex-1 overflow-y-auto bg-[#FAFAFB] px-3 py-5 dark:bg-[#111111] md:px-6'>
                    <div className='mx-auto flex min-h-full w-full max-w-[940px] flex-col'>
                        {messages.length === 0 && !isWaiting && (
                            <div className='flex flex-1 items-center justify-center'>
                                <div className='flex h-16 w-16 items-center justify-center rounded-full border border-gray-200 bg-white text-lg font-bold text-[#00330f] shadow-sm dark:border-borderColor-darkMode dark:bg-black dark:text-white'>
                                    Leo
                                </div>
                            </div>
                        )}

                        <LeoChatMessages
                            messages={messages}
                            clients={clients}
                            selectedClients={selectedClients}
                            properties={properties}
                            loadingClients={loadingClients}
                            loadingProperties={loadingProperties}
                            propertiesLoadError={propertiesLoadError}
                            savingClientSelect={savingClientSelect}
                            savingPropertySelect={savingPropertySelect}
                            onClickOption={onClickOption}
                            onSaveClients={onSaveClients}
                            onSaveProperties={onSaveProperties}
                        />

                        {isWaiting && (
                            <div className='flex items-start gap-3 py-2'>
                                <LeoAvatar size='sm' />
                                <div className='rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 shadow-sm dark:border-borderColor-darkMode dark:bg-black dark:text-gray-300'>
                                    Leo is preparing your answer...
                                </div>
                            </div>
                        )}
                        <div ref={bottomRef} />
                    </div>
                </div>

                <div className='border-t border-gray-200 bg-white px-3 py-3 dark:border-borderColor-darkMode dark:bg-black md:px-6'>
                    <div className='mx-auto w-full max-w-[940px]'>
                        <LeoChatInput
                            placeholder='Ask Leo for something'
                            topicId={topicId}
                            stage={lastAgentMessage?.stage}
                            options={lastAgentMessage?.options}
                            disabled={isWaiting || savingClientSelect || savingPropertySelect}
                            onClickOption={onClickOption}
                            onSend={sendMessage}
                            onUploadFiles={uploadFiles}
                        />
                    </div>
                </div>
            </div>

            {toast?.message && (
                <Toast
                    type={toast.type}
                    text={toast.message}
                    onHandleCloseToast={() => setToast(null)}
                />
            )}
        </>
    );
};

export default LeoChatPage;
